/* eslint-disable */
import React, { useLayoutEffect, useRef, useState } from "react";
import moment from "moment";
import { RRule } from "rrule";
import { makeStyles } from "@material-ui/styles";
import {
	Button,
	Checkbox,
	Card,
	CardContent,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	FormControl,
	FormHelperText,
	Grid,
	InputLabel,
	ListItemText,
	MenuItem,
	Modal,
	Select,
	TextField,
	Typography,
} from "@material-ui/core";
import { ValidatorForm, TextValidator, SelectValidator } from "react-material-ui-form-validator";
import {
	FormActions,
	FormButton,
	FormSubmitButton,
	SELECT_MENU_PROPS,
} from "frame/components";
import { useTerminology } from "utils";
import { safetyWiseApi } from "utils/safetyWiseApi";
import { useSelector } from "react-redux";
import EquipmentMultiSelect from "features/forms/components/equipment-multiselect";
import {
	DEFAULT_TASK_STATUS,
	TASK_STATUS_OPTIONS,
	normalizeTaskStatus,
} from "../task-status";
import {
	buildStaffTaskPayloads,
	selectedStaffIdsForSave,
	taskCreateSuccessMessage,
} from "../task-staff-selection";

const useModalStyles = makeStyles((theme) => ({
	root: {
		position: "absolute",
		top: "50%",
		left: "50%",
		transform: "translate(-50%, -50%)",
		outline: "none",
		boxShadow: theme.shadows[20],
		width: 700,
		maxHeight: "100%",
		overflowY: "auto",
		maxWidth: "100%",
	},
	container: {
		marginTop: theme.spacing(3),
	},
	actions: {
		justifyContent: "flex-end",
		marginTop: theme.spacing(3),
	},
	emptyOption: {
		display: "none",
	},
	fieldLabel: {
		display: "block",
		marginBottom: theme.spacing(0.75),
		color: theme.palette.text.secondary,
		fontWeight: theme.typography.fontWeightMedium,
	},
	staffSelect: {
		whiteSpace: "normal",
	},
}));

const MAX_CREATED_ENTRIES = 90;

export const defaultScheduleForm = {
	id: "",
	staffId: "",
	staffIds: [],
	siteId: "",
	title: "",
	notes: "",
	startsAt: "",
	endsAt: "",
	status: DEFAULT_TASK_STATUS,
	isRecurring: false,
	recurrenceFrequency: "WEEKLY",
	recurrenceUntil: "",
	equipment: [],
	captureEquipmentUsage: false,
};

const toInputDateTime = (dateLike) => moment(dateLike).local().format("YYYY-MM-DD[T]HH:mm");
const toUtcIso = (localDateTimeValue) => moment(localDateTimeValue).utc().toISOString();
const getApiErrorMessage = (error, fallback) =>
	error?.response?.data?.message || error?.response?.data?.error || fallback;

/** Returns undefined when the value cannot be read (never treat that as ""). */
const readChangeValue = (e) => {
	if (e == null) return undefined;
	const target = e.target ?? e.currentTarget;
	if (target != null) return target.value;
	if (typeof e === "string") return e;
	return undefined;
};

const buildFormFromEntry = (entry) => ({
	...defaultScheduleForm,
	id: entry.id,
	staffId: entry.staffId || "",
	staffIds: entry.staffId ? [entry.staffId] : [],
	siteId: entry.siteId || "",
	title: entry.title || "",
	notes: entry.notes || "",
	startsAt: toInputDateTime(entry.startsAtUTC),
	endsAt: toInputDateTime(entry.endsAtUTC),
	status: normalizeTaskStatus(entry.status),
	equipment: entry.equipment || [],
	captureEquipmentUsage: false,
});

const getDefaultTaskWindow = () => {
	const start = moment().startOf("day").add(9, "hours");
	return { start, end: start.clone().add(1, "hour") };
};

const buildDefaultCreateForm = (defaultStaffId, initialStart, initialEnd, simpleMode = false) => {
	const start = initialStart
		? moment(initialStart)
		: simpleMode
			? getDefaultTaskWindow().start
			: moment().startOf("hour");
	const end = initialEnd
		? moment(initialEnd)
		: simpleMode
			? getDefaultTaskWindow().end
			: start.clone().add(1, "hour");
	return {
		...defaultScheduleForm,
		staffId: defaultStaffId || "",
		staffIds: defaultStaffId ? [defaultStaffId] : [],
		startsAt: toInputDateTime(start),
		endsAt: toInputDateTime(end),
	};
};

export const ScheduleEntryDialog = ({
	open,
	onClose,
	organisationId,
	entry = null,
	initialStart = null,
	initialEnd = null,
	defaultStaffId = "",
	staffLocked = false,
	staffOptions = [],
	siteOptions = [],
	onSaved,
	onNotify,
	createTitle = "Add Schedule Entry",
	editTitle = "Edit Schedule Entry",
	createSuccessMessage = "Schedule created.",
	updateSuccessMessage = "Schedule updated.",
	saveSuccessMessage = "Task saved successfully",
	cancelSuccessMessage = "Schedule cancelled.",
	cancelReason = "Cancelled from planner",
	showStartTimesheet = false,
	onStartTimesheet,
	showCreateAdminTimesheet = false,
	onCreateAdminTimesheet,
	simpleMode = false,
}) => {
	const modalClasses = useModalStyles();
	const { siteLabel } = useTerminology();
	const equipmentEnabled = useSelector(({ featureFlags }) => featureFlags?.equipment === true);
	const equipmentUsageRecordingEnabled = useSelector(
		({ featureFlags }) => featureFlags?.equipmentUsageRecording === true
	);
	const equipmentList = useSelector(({ equipment }) => equipment?.data || []);
	const [form, setForm] = useState(defaultScheduleForm);
	const isEditing = !!form.id;
	const openSessionRef = useRef({ open: false, entryId: null });

	const setFormField = (field) => (e) => {
		const value = readChangeValue(e);
		if (value === undefined) return;
		setForm((prev) => ({ ...prev, [field]: value }));
	};

	// Initialise form when the dialog opens or when switching tasks — not when
	// defaultStaffId updates after staff/sites finish loading (that remounts fields
	// mid-keystroke and leaves e.target null in onChange handlers).
	useLayoutEffect(() => {
		if (!open) {
			openSessionRef.current = { open: false, entryId: null };
			return;
		}

		const entryId = entry?.id || null;
		const session = openSessionRef.current;
		const isNewSession = !session.open || session.entryId !== entryId;
		if (!isNewSession) return;

		openSessionRef.current = { open: true, entryId };

		if (entryId) {
			setForm(buildFormFromEntry(entry));
		} else {
			setForm(buildDefaultCreateForm(defaultStaffId, initialStart, initialEnd, simpleMode));
		}
	}, [open, entry, defaultStaffId, initialStart, initialEnd, simpleMode]);

	const allowMultipleStaff = !isEditing && !staffLocked;

	const saveEntry = async () => {
		const selectedStaffIds = selectedStaffIdsForSave({
			isEditing,
			staffLocked,
			staffId: form.staffId,
			staffIds: form.staffIds,
		});
		if (selectedStaffIds.length === 0 || !form.title) {
			onNotify?.({
				message: simpleMode ? "Please fill staff and title." : "Please fill staff, title, start and end.",
				severity: "error",
			});
			return;
		}

		let startsAt = form.startsAt;
		let endsAt = form.endsAt;
		if (simpleMode && (!startsAt || !endsAt)) {
			const { start, end } = getDefaultTaskWindow();
			startsAt = toInputDateTime(start);
			endsAt = toInputDateTime(end);
		} else if (!startsAt || !endsAt) {
			onNotify?.({ message: "Please fill staff, title, start and end.", severity: "error" });
			return;
		}

		const payload = {
			id: form.id || undefined,
			organisationId,
			staffId: selectedStaffIds[0],
			siteId: form.siteId || null,
			title: form.title.trim(),
			notes: form.notes || "",
			startsAtUTC: toUtcIso(startsAt),
			endsAtUTC: toUtcIso(endsAt),
			status: form.status,
			equipment: (form.equipment || []).map((item) => ({
				organisationEquipmentId: item.organisationEquipmentId,
				notes: item.notes || "",
				captureUsage: !!form.captureEquipmentUsage,
				startReading: item.startReading,
				endReading: item.endReading,
				unitsUsed: item.unitsUsed,
				updateCurrentReading: !!item.updateCurrentReading,
			})),
		};

		try {
			if (!simpleMode && form.isRecurring && !isEditing) {
				if (!form.recurrenceUntil) {
					onNotify?.({ message: "Select a recurrence end date/time.", severity: "error" });
					return;
				}

				const rule = new RRule({
					freq: form.recurrenceFrequency === "DAILY" ? RRule.DAILY : RRule.WEEKLY,
					dtstart: moment(form.startsAt).toDate(),
					until: moment(form.recurrenceUntil).toDate(),
				});
				const dates = rule.all();
				const durationMinutes = Math.max(
					15,
					moment(form.endsAt).diff(moment(form.startsAt), "minutes")
				);

				const createdCount = dates.length * selectedStaffIds.length;
				if (createdCount > MAX_CREATED_ENTRIES) {
					onNotify?.({
						message: "Please use a shorter recurrence window (max 90 entries).",
						severity: "error",
					});
					return;
				}

				const staffPayloads = buildStaffTaskPayloads(payload, selectedStaffIds, {
					recordUsageOnFirst: false,
				});
				await Promise.all(
					dates.flatMap((startDate) => {
						const startMoment = moment(startDate);
						const endMoment = moment(startDate).add(durationMinutes, "minutes");
						return staffPayloads.map((staffPayload) =>
							safetyWiseApi.post(`${organisationId}/schedule/create`, {
								...staffPayload,
								startsAtUTC: startMoment.utc().toISOString(),
								endsAtUTC: endMoment.utc().toISOString(),
							})
						);
					})
				);
				onNotify?.({
					message: `Created ${createdCount} recurring schedule entries.`,
					severity: "success",
				});
			} else {
				if (isEditing) {
					await safetyWiseApi.put(`${organisationId}/schedule/edit/${form.id}`, payload);
					onNotify?.({
						message: simpleMode ? saveSuccessMessage : updateSuccessMessage,
						severity: "success",
					});
				} else {
					const staffPayloads = buildStaffTaskPayloads(payload, selectedStaffIds);
					await Promise.all(
						staffPayloads.map((staffPayload) =>
							safetyWiseApi.post(`${organisationId}/schedule/create`, staffPayload)
						)
					);
					onNotify?.({
						message: taskCreateSuccessMessage(staffPayloads.length, {
							simpleMode,
							singleMessage: simpleMode ? saveSuccessMessage : createSuccessMessage,
						}),
						severity: "success",
					});
				}
			}
			onClose();
			onSaved?.();
		} catch (error) {
			onNotify?.({
				message: getApiErrorMessage(
					error,
					simpleMode ? "Unable to save task." : "Unable to save schedule entry."
				),
				severity: "error",
			});
		}
	};

	const cancelEntry = async () => {
		if (!form.id) return;
		try {
			await safetyWiseApi.post(`${organisationId}/schedule/cancel/${form.id}`, {
				reason: cancelReason,
			});
			onNotify?.({ message: cancelSuccessMessage, severity: "success" });
			onClose();
			onSaved?.();
		} catch (error) {
			onNotify?.({
				message: getApiErrorMessage(error, "Unable to cancel schedule entry."),
				severity: "error",
			});
		}
	};

	const equipmentOptions = (equipmentList || [])
		.filter((eq) => eq.active !== false)
		.map((eq) => ({
			label: eq.equipment,
			value: eq.id,
			maintenanceType: eq.maintenanceType,
			currentHoursKms: eq.currentHoursKms,
		}))
		.sort((a, b) => (a.label > b.label ? 1 : -1));

	const renderEquipmentFields = () => {
		if (!equipmentEnabled || equipmentOptions.length === 0) return null;
		const showCapture =
			equipmentUsageRecordingEnabled &&
			(simpleMode || isEditing || !form.isRecurring);
		return (
			<>
				{showCapture && (
					<FormControlLabel
						control={
							<Checkbox
								checked={!!form.captureEquipmentUsage}
								onChange={(e) =>
									setForm((prev) => ({
										...prev,
										captureEquipmentUsage: e.target.checked,
										equipment: (prev.equipment || []).map((item) => ({
											...item,
											captureUsage: e.target.checked,
										})),
									}))
								}
								color="primary"
							/>
						}
						label="Capture equipment usage"
					/>
				)}
				<EquipmentMultiSelect
					options={equipmentOptions}
					value={form.equipment || []}
					selectLabel="Equipment for this task"
					captureUsage={showCapture && !!form.captureEquipmentUsage}
					onChange={(equipment) =>
						setForm((prev) => ({
							...prev,
							equipment: equipment.map((item) => ({
								...item,
								captureUsage: showCapture && !!prev.captureEquipmentUsage,
							})),
						}))
					}
				/>
			</>
		);
	};

	if (simpleMode) {
		const modalTitle = isEditing ? editTitle : createTitle;
		return (
			<Modal open={open} onClose={onClose}>
				<Card className={modalClasses.root}>
					<ValidatorForm autoComplete="off" noValidate onSubmit={saveEntry}>
						<CardContent>
							<Typography align="center" gutterBottom variant="h3">
								{modalTitle}
							</Typography>
							<Grid className={modalClasses.container} container spacing={3}>
								<Grid item xs={12}>
									<Typography
										className={modalClasses.fieldLabel}
										component="label"
										htmlFor="task-staff"
										variant="body2"
									>
										Staff *
									</Typography>
									{allowMultipleStaff ? (
										<FormControl variant="outlined" fullWidth>
											<Select
												fullWidth
												multiple
												displayEmpty
												id="task-staff"
												name="staffIds"
												classes={{ select: modalClasses.staffSelect }}
												value={form.staffIds || []}
												disabled={staffLocked}
												MenuProps={SELECT_MENU_PROPS}
												onChange={setFormField("staffIds")}
												renderValue={(selected) => {
													if (!selected?.length) return "Select staff";
													return selected
														.map(
															(id) =>
																staffOptions.find((staff) => staff.id === id)?.name ||
																""
														)
														.filter(Boolean)
														.join(", ");
												}}
											>
												{staffOptions.map((staff) => (
													<MenuItem key={staff.id} value={staff.id}>
														<Checkbox
															checked={(form.staffIds || []).indexOf(staff.id) > -1}
															color="primary"
															style={{ pointerEvents: "none" }}
														/>
														<ListItemText primary={staff.name} />
													</MenuItem>
												))}
											</Select>
											<FormHelperText>
												A separate task is created for each selected staff member.
											</FormHelperText>
										</FormControl>
									) : (
										<SelectValidator
											fullWidth
											id="task-staff"
											name="staffId"
											select
											SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
											value={form.staffId || ""}
											variant="outlined"
											validators={["required"]}
											errorMessages={["This field is required"]}
											disabled={staffLocked}
											onChange={setFormField("staffId")}
										>
											<MenuItem
												value=""
												className={modalClasses.emptyOption}
												disabled
											/>
											{staffOptions.map((staff) => (
												<MenuItem key={staff.id} value={staff.id}>
													{staff.name}
												</MenuItem>
											))}
										</SelectValidator>
									)}
								</Grid>
								<Grid item xs={12}>
									<Typography
										className={modalClasses.fieldLabel}
										component="label"
										htmlFor="task-title"
										variant="body2"
									>
										Title *
									</Typography>
									<TextValidator
										fullWidth
										id="task-title"
										name="title"
										value={form.title}
										variant="outlined"
										validators={["required"]}
										errorMessages={["This field is required"]}
										onChange={setFormField("title")}
									/>
								</Grid>
								<Grid item xs={12}>
									<Typography
										className={modalClasses.fieldLabel}
										component="label"
										htmlFor="task-site"
										variant="body2"
									>
										{siteLabel}
									</Typography>
									<SelectValidator
										fullWidth
										id="task-site"
										name="siteId"
										select
										SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
										value={form.siteId || ""}
										variant="outlined"
										onChange={setFormField("siteId")}
									>
										<MenuItem value="">
											<em>None</em>
										</MenuItem>
										{siteOptions.map((site) => (
											<MenuItem key={site.id} value={site.id}>
												{site.name}
											</MenuItem>
										))}
									</SelectValidator>
								</Grid>
								{equipmentEnabled && (
									<Grid item xs={12}>
										{renderEquipmentFields()}
									</Grid>
								)}
								<Grid item xs={12}>
									<Typography
										className={modalClasses.fieldLabel}
										component="label"
										htmlFor="task-notes"
										variant="body2"
									>
										Notes
									</Typography>
									<TextField
										fullWidth
										id="task-notes"
										name="notes"
										value={form.notes}
										variant="outlined"
										multiline
										rows={3}
										onChange={setFormField("notes")}
									/>
								</Grid>
								<Grid item xs={12}>
									<Typography
										className={modalClasses.fieldLabel}
										component="label"
										htmlFor="task-status"
										variant="body2"
									>
										Status
									</Typography>
									<SelectValidator
										fullWidth
										id="task-status"
										name="status"
										select
										SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
										value={form.status}
										variant="outlined"
										onChange={setFormField("status")}
									>
										{TASK_STATUS_OPTIONS.map((option) => (
											<MenuItem key={option.value} value={option.value}>
												{option.label}
											</MenuItem>
										))}
									</SelectValidator>
								</Grid>
							</Grid>
							<FormActions className={modalClasses.actions}>
								<FormSubmitButton>Save</FormSubmitButton>
								<FormButton onClick={onClose}>Cancel</FormButton>
							</FormActions>
						</CardContent>
					</ValidatorForm>
				</Card>
			</Modal>
		);
	}

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>{isEditing ? editTitle : createTitle}</DialogTitle>
			<DialogContent>
				<FormControl variant="outlined" margin="dense" fullWidth>
					<InputLabel id="schedule-staff-label" shrink={allowMultipleStaff || undefined}>
						Staff
					</InputLabel>
					{allowMultipleStaff ? (
						<Select
							labelId="schedule-staff-label"
							multiple
							displayEmpty
							classes={{ select: modalClasses.staffSelect }}
							value={form.staffIds || []}
							onChange={setFormField("staffIds")}
							label="Staff"
							disabled={staffLocked}
							MenuProps={SELECT_MENU_PROPS}
							renderValue={(selected) => {
								if (!selected?.length) return "Select staff";
								return selected
									.map((id) => staffOptions.find((staff) => staff.id === id)?.name || "")
									.filter(Boolean)
									.join(", ");
							}}
						>
							{staffOptions.map((staff) => (
								<MenuItem key={staff.id} value={staff.id}>
									<Checkbox
										checked={(form.staffIds || []).indexOf(staff.id) > -1}
										color="primary"
										style={{ pointerEvents: "none" }}
									/>
									<ListItemText primary={staff.name} />
								</MenuItem>
							))}
						</Select>
					) : (
						<Select
							labelId="schedule-staff-label"
							value={form.staffId}
							onChange={(e) => setForm((prev) => ({ ...prev, staffId: e.target.value }))}
							label="Staff"
							disabled={staffLocked}
							MenuProps={SELECT_MENU_PROPS}
						>
							{staffOptions.map((staff) => (
								<MenuItem key={staff.id} value={staff.id}>
									{staff.name}
								</MenuItem>
							))}
						</Select>
					)}
					{allowMultipleStaff && (
						<FormHelperText>
							A separate entry is created for each selected staff member.
						</FormHelperText>
					)}
				</FormControl>
				<TextField
					margin="dense"
					label="Title"
					value={form.title}
					onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
					fullWidth
					variant="outlined"
				/>
				<FormControl variant="outlined" margin="dense" fullWidth>
					<InputLabel id="schedule-site-label" shrink>
						Site
					</InputLabel>
					<Select
						labelId="schedule-site-label"
						value={form.siteId || ""}
						onChange={(e) => setForm((prev) => ({ ...prev, siteId: e.target.value }))}
						label="Site"
						MenuProps={SELECT_MENU_PROPS}
						displayEmpty
						renderValue={(selected) => {
							if (!selected) return "";
							const site = siteOptions.find((s) => s.id === selected);
							return site?.name || "";
						}}
					>
						<MenuItem value="">
							<em>None</em>
						</MenuItem>
						{siteOptions.map((site) => (
							<MenuItem key={site.id} value={site.id}>
								{site.name}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				{equipmentEnabled && renderEquipmentFields()}
				<TextField
					margin="dense"
					label="Notes"
					value={form.notes}
					onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
					fullWidth
					multiline
					rows={3}
					variant="outlined"
				/>
				{!simpleMode && (
					<>
						<TextField
							margin="dense"
							label="Start"
							type="datetime-local"
							value={form.startsAt}
							onChange={(e) => setForm((prev) => ({ ...prev, startsAt: e.target.value }))}
							fullWidth
							variant="outlined"
							InputLabelProps={{ shrink: true }}
						/>
						<TextField
							margin="dense"
							label="End"
							type="datetime-local"
							value={form.endsAt}
							onChange={(e) => setForm((prev) => ({ ...prev, endsAt: e.target.value }))}
							fullWidth
							variant="outlined"
							InputLabelProps={{ shrink: true }}
						/>
					</>
				)}
				<FormControl variant="outlined" margin="dense" fullWidth>
					<InputLabel id="schedule-status-label">Status</InputLabel>
					<Select
						labelId="schedule-status-label"
						value={form.status}
						onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
						label="Status"
						MenuProps={SELECT_MENU_PROPS}
					>
						{TASK_STATUS_OPTIONS.map((option) => (
							<MenuItem key={option.value} value={option.value}>
								{option.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				{!simpleMode && !isEditing && (
					<>
						<FormControlLabel
							control={
								<Checkbox
									checked={form.isRecurring}
									onChange={(e) =>
										setForm((prev) => ({ ...prev, isRecurring: e.target.checked }))
									}
									color="primary"
								/>
							}
							label="Make recurring template (Option A)"
						/>
						{form.isRecurring && (
							<>
								<FormControl variant="outlined" margin="dense" fullWidth>
									<InputLabel id="schedule-recur-frequency-label">Repeat</InputLabel>
									<Select
										labelId="schedule-recur-frequency-label"
										value={form.recurrenceFrequency}
										onChange={(e) =>
											setForm((prev) => ({
												...prev,
												recurrenceFrequency: e.target.value,
											}))
										}
										label="Repeat"
										MenuProps={SELECT_MENU_PROPS}
									>
										<MenuItem value="DAILY">Daily</MenuItem>
										<MenuItem value="WEEKLY">Weekly</MenuItem>
									</Select>
								</FormControl>
								<TextField
									margin="dense"
									label="Repeat Until"
									type="datetime-local"
									value={form.recurrenceUntil}
									onChange={(e) =>
										setForm((prev) => ({
											...prev,
											recurrenceUntil: e.target.value,
										}))
									}
									fullWidth
									variant="outlined"
									InputLabelProps={{ shrink: true }}
								/>
							</>
						)}
					</>
				)}
			</DialogContent>
			<DialogActions>
				{showCreateAdminTimesheet && entry && onCreateAdminTimesheet && (
					<Button onClick={() => onCreateAdminTimesheet(entry)} color="primary">
						Admin Timesheet
					</Button>
				)}
				{showStartTimesheet && entry && onStartTimesheet && (
					<Button onClick={() => onStartTimesheet(entry)} color="primary">
						Start Timesheet
					</Button>
				)}
				{isEditing && !simpleMode && (
					<Button onClick={cancelEntry} color="secondary">
						Cancel entry
					</Button>
				)}
				<Button onClick={onClose}>{simpleMode ? "Close" : "Cancel"}</Button>
				<Button onClick={saveEntry} color="primary" variant="contained">
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ScheduleEntryDialog;
